"use client";

import { useEffect } from "react";

const HEADER_SOLID_SCROLL_Y = 16;
const HEADER_HIDE_SCROLL_Y = 112;
const HEADER_HIDE_DELTA = 24;
const HEADER_REVEAL_DELTA = 96;
const HEADER_TOP_REVEAL_Y = 48;
const SCROLL_NOISE_DELTA = 2;

export default function MobileOverlayHeaderBehavior() {
  useEffect(() => {
    const header = document.querySelector<HTMLElement>(
      "[data-mobile-overlay-header]",
    );
    if (!header) return;

    const mediaQuery = window.matchMedia("(max-width: 1279px)");
    const documentElement = document.documentElement;
    let previousScrollY = Math.max(0, window.scrollY);
    let directionBaselineScrollY = previousScrollY;
    let scrollDirection: "down" | "up" | null = null;
    let headerHidden = false;
    let hiddenScrollY = previousScrollY;

    const getVisibleHeaderOffset = () =>
      `${Math.ceil(header.getBoundingClientRect().height)}px`;

    const applyHeaderState = (isHidden: boolean, isSolid: boolean) => {
      header.dataset.headerSolid = String(isSolid);
      header.dataset.headerHidden = String(isHidden);
      documentElement.style.setProperty(
        "--mobile-overlay-header-visible-offset",
        getVisibleHeaderOffset(),
      );
    };

    const updateHeader = () => {
      const currentScrollY = Math.max(0, window.scrollY);

      if (!mediaQuery.matches) {
        headerHidden = false;
        hiddenScrollY = currentScrollY;
        scrollDirection = null;
        directionBaselineScrollY = currentScrollY;
        applyHeaderState(false, false);
        documentElement.style.removeProperty("--mobile-overlay-header-offset");
        documentElement.style.removeProperty(
          "--mobile-overlay-header-visible-offset",
        );
        previousScrollY = currentScrollY;
        return;
      }

      const scrollDelta = currentScrollY - previousScrollY;
      const isSolid = currentScrollY > HEADER_SOLID_SCROLL_Y;
      let shouldHide = headerHidden;

      if (currentScrollY <= HEADER_TOP_REVEAL_Y) {
        shouldHide = false;
        hiddenScrollY = currentScrollY;
        scrollDirection = null;
        directionBaselineScrollY = currentScrollY;
      } else if (Math.abs(scrollDelta) >= SCROLL_NOISE_DELTA) {
        const nextDirection = scrollDelta > 0 ? "down" : "up";

        if (nextDirection !== scrollDirection) {
          scrollDirection = nextDirection;
          directionBaselineScrollY = previousScrollY;
        }

        const directionDistance = Math.abs(
          currentScrollY - directionBaselineScrollY,
        );

        if (
          nextDirection === "down" &&
          currentScrollY > HEADER_HIDE_SCROLL_Y &&
          directionDistance >= HEADER_HIDE_DELTA
        ) {
          shouldHide = true;
          hiddenScrollY = currentScrollY;
        } else if (nextDirection === "down" && headerHidden) {
          hiddenScrollY = Math.max(hiddenScrollY, currentScrollY);
        }

        if (
          nextDirection === "up" &&
          headerHidden &&
          hiddenScrollY - currentScrollY >= HEADER_REVEAL_DELTA
        ) {
          shouldHide = false;
        }
      }

      headerHidden = shouldHide;
      applyHeaderState(headerHidden, isSolid);
      previousScrollY = currentScrollY;
    };

    updateHeader();
    window.addEventListener("scroll", updateHeader, { passive: true });
    mediaQuery.addEventListener("change", updateHeader);

    return () => {
      window.removeEventListener("scroll", updateHeader);
      mediaQuery.removeEventListener("change", updateHeader);
      documentElement.style.removeProperty("--mobile-overlay-header-offset");
      documentElement.style.removeProperty(
        "--mobile-overlay-header-visible-offset",
      );
    };
  }, []);

  return null;
}
